const findComparison = (value) => {
    if (!value || typeof value !== "object") {
        return null;
    }

    if (Array.isArray(value)) {
        for (const item of value) {
            const comparison = findComparison(item);
            if (comparison) return comparison;
        }
        return null;
    }

    if (Array.isArray(value.headers) && Array.isArray(value.rows)) {
        return value;
    }

    for (const key of ["cost_comparison", "scenario_comparison", "detailed_comparison", "comparison_table"]) {
        const comparison = value[key];
        if (comparison && typeof comparison === "object" && !Array.isArray(comparison)) {
            const rows = comparison.rows || comparison.data || comparison.values;
            const headers = comparison.headers || (Array.isArray(rows) && rows[0] && !Array.isArray(rows[0])
                ? Object.keys(rows[0])
                : null);
            if (Array.isArray(headers) && Array.isArray(rows)) return { headers, rows };
        }
    }

    for (const nestedValue of Object.values(value)) {
        const comparison = findComparison(nestedValue);
        if (comparison) return comparison;
    }

    return null;
};

const findJsonEnd = (text, start) => {
    const stack = [];
    let inString = false;
    let escaped = false;

    for (let index = start; index < text.length; index += 1) {
        const character = text[index];
        if (inString) {
            if (escaped) escaped = false;
            else if (character === "\\") escaped = true;
            else if (character === '"') inString = false;
        } else if (character === '"') {
            inString = true;
        } else if (character === "{" || character === "[") {
            stack.push(character);
        } else if (character === "}" || character === "]") {
            const opening = stack.pop();
            if ((character === "}" && opening !== "{") || (character === "]" && opening !== "[")) return -1;
            if (stack.length === 0) return index + 1;
        }
    }

    return -1;
};

const toCell = (value) => String(value ?? "")
    .replace(/\|/g, "\\|")
    .replace(/\r?\n/g, " ");

const comparisonToMarkdown = ({ headers, rows }) => [
    `| ${headers.map(toCell).join(" | ")} |`,
    `| ${headers.map(() => "---").join(" | ")} |`,
    ...rows.map((row) => {
        const cells = Array.isArray(row)
            ? row
            : headers.map((header, index) => row?.[header] ?? Object.values(row || {})[index] ?? "");
        return `| ${cells.map(toCell).join(" | ")} |`;
    }),
].join("\n");

const rowsToMarkdown = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0 || !rows[0] || typeof rows[0] !== "object") {
        return "";
    }

    const headers = Object.keys(rows[0]);
    return comparisonToMarkdown({ headers, rows });
};

export const formatWhatIfResponse = (response) => {
    if (!response || typeof response !== "object" || Array.isArray(response)) {
        return "";
    }

    const comparisonTable = response.comparison_table;
    const comparisonSections = [];
    if (typeof comparisonTable === "string") {
        comparisonSections.push(comparisonTable);
    } else if (comparisonTable && typeof comparisonTable === "object") {
        const parameters = rowsToMarkdown(comparisonTable.parameters);
        const costComparison = rowsToMarkdown(comparisonTable.cost_comparison);

        if (parameters) comparisonSections.push(`### Scenario Parameters\n\n${parameters}`);
        if (costComparison) comparisonSections.push(`### Cost Comparison\n\n${costComparison}`);
        if (
            comparisonSections.length === 0 &&
            Array.isArray(comparisonTable.headers) &&
            Array.isArray(comparisonTable.rows)
        ) {
            comparisonSections.push(comparisonToMarkdown(comparisonTable));
        }
    }

    return [...comparisonSections, response.key_insight].filter(Boolean).join("\n\n");
};

const convertJson = (json) => {
    try {
        const comparison = findComparison(JSON.parse(json));
        return comparison ? comparisonToMarkdown(comparison) : null;
    } catch {
        return null;
    }
};

export const replaceComparisonJson = (text) => {
    let result = text.replace(/```(?:json)?\s*([\s\S]*?)```/gi, (block, json) =>
        convertJson(json.trim()) ?? block
    );

    for (let start = 0; start < result.length; start += 1) {
        if (result[start] !== "{" && result[start] !== "[") continue;
        const end = findJsonEnd(result, start);
        if (end === -1) continue;

        const markdown = convertJson(result.slice(start, end));
        if (markdown) {
            result = `${result.slice(0, start)}${markdown}${result.slice(end)}`;
            start += markdown.length - 1;
        }
    }

    return result;
};
